import React from 'react';
import { Star, Quote, Zap, ShieldCheck, TrendingUp, HeartHandshake } from 'lucide-react';
import { Testimonial } from '../types';

const Testimonials: React.FC = () => {
  const testimonials: Testimonial[] = [
    {
      quote: "Launching my D2C brand in Bangalore was seamless. They guided me through FSSAI compliance and the low MOQs helped me test 3 flavors without burning cash.",
      author: "Rahul S.",
      role: "Founder, Zenith Nutrition (India)"
    },
    {
      quote: "BuildMySupplement simplified our supply chain. We used to manufacture in the US, but switching to their Indian facility cut our costs by 40% without compromising on quality.",
      author: "Ahmed R.",
      role: "Nutrition Brand Owner, Dubai, UAE"
    },
    {
      quote: "Reliability is everything. We order 50k units a month for our Mumbai and Delhi distribution. Every batch arrives on time with complete documentation.",
      author: "Vikram T.",
      role: "Director, ProFit India"
    },
    {
        quote: "As a UK-based brand, we were worried about compliance. Their team handled the regulatory documentation perfectly. The Ayurvedic line we developed is a bestseller in London.",
        author: "Sarah J.",
        role: "Wellness Startup Founder, London, UK"
    },
    {
        quote: "The R&D team is excellent. They helped us formulate a specific protein blend for the Indian palate that isn't too sweet. Customer feedback has been phenomenal.",
        author: "Anjali K.",
        role: "Brand Manager, Vitality Labs"
    },
    {
        quote: "Exporting to 4 countries is complex. BuildMySupplement delivers GMP certification and consistent quality that meets international standards for every single batch.",
        author: "Rohit D.",
        role: "Global Export Manager"
    }
  ];

  return (
    <section id="testimonials" className="py-24 bg-orange-50 border-b border-orange-100/50 relative overflow-hidden">
      {/* Decorative background blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-white/40 rounded-full blur-3xl -ml-20 -mt-20 pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl -mr-20 -mb-20 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-orange-100/80 border border-orange-200 mb-6">
            <span className="text-orange-800 text-xs font-bold uppercase tracking-widest">
                Client Success Stories
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 tracking-tight">
            Trusted in India & Abroad
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Powering fast-growing D2C brands in India and established labels in USA, Europe, and UAE.
          </p>
        </div>

        {/* Masonry-ish Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative group border border-orange-100/50">
              <Quote className="absolute top-8 right-8 text-orange-100 w-12 h-12 rotate-180 group-hover:text-orange-200 transition-colors" />
              <div className="flex text-orange-500 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 text-lg mb-8 relative z-10 leading-relaxed">"{t.quote}"</p>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold mr-4 text-sm">
                    {t.author.charAt(0)}
                </div>
                <div>
                    <p className="font-bold text-gray-900">{t.author}</p>
                    <p className="text-sm text-gray-500">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Value Props - Enhanced */}
        <div className="border-t border-orange-200 pt-16 relative">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-orange-300 rounded-b-full opacity-50"></div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12 text-center">
                <div className="group">
                    <div className="w-16 h-16 mx-auto bg-white rounded-2xl shadow-sm border border-orange-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 group-hover:shadow-md group-hover:border-orange-200">
                        <Zap className="w-8 h-8 text-orange-500" strokeWidth={1.5} fill="currentColor" fillOpacity={0.1} />
                    </div>
                    <h3 className="font-extrabold text-2xl text-gray-900 mb-2">Speed</h3>
                    <p className="text-sm text-gray-600 font-medium bg-orange-100/50 inline-block px-3 py-1 rounded-full">Fast Turnaround</p>
                </div>
                
                <div className="group">
                    <div className="w-16 h-16 mx-auto bg-white rounded-2xl shadow-sm border border-orange-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 group-hover:shadow-md group-hover:border-orange-200">
                        <ShieldCheck className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-extrabold text-2xl text-gray-900 mb-2">Quality</h3>
                    <p className="text-sm text-gray-600 font-medium bg-orange-100/50 inline-block px-3 py-1 rounded-full">FSSAI & GMP</p>
                </div>
                
                <div className="group">
                    <div className="w-16 h-16 mx-auto bg-white rounded-2xl shadow-sm border border-orange-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 group-hover:shadow-md group-hover:border-orange-200">
                        <TrendingUp className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-extrabold text-2xl text-gray-900 mb-2">Scale</h3>
                    <p className="text-sm text-gray-600 font-medium bg-orange-100/50 inline-block px-3 py-1 rounded-full">Domestic & Export</p>
                </div>
                
                <div className="group">
                    <div className="w-16 h-16 mx-auto bg-white rounded-2xl shadow-sm border border-orange-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 group-hover:shadow-md group-hover:border-orange-200">
                        <HeartHandshake className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-extrabold text-2xl text-gray-900 mb-2">Support</h3>
                    <p className="text-sm text-gray-600 font-medium bg-orange-100/50 inline-block px-3 py-1 rounded-full">24/7 Team</p>
                </div>
            </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;